import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ProfesionalRepository } from 'src/app/domain/service/profesional/profesional-repository';
import { ProfesionalPageResponse } from 'src/app/domain/model/profesional/profesional';

/**
 * Adaptador HTTP para el repositorio de Profesionales
 *
 * Implementa el puerto ProfesionalRepository para comunicación HTTP con backend.
 * Realiza llamadas REST al backend Spring Boot (ProfesionalController en servicios-ms).
 *
 * Arquitectura Hexagonal:
 * - Este adaptador está en infraestructura
 * - Implementa el puerto ProfesionalRepository del dominio
 * - Trabaja con la entidad de dominio Profesional
 *
 * Consume de servicios-ms (puerto 8080), no de agenda-ms (puerto 8081)
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Injectable({ providedIn: 'root' })
export class HttpProfesionalRepository implements ProfesionalRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/profesionales`;
  private static readonly PAGE_SIZE_MIN = 1;
  private static readonly PAGE_SIZE_MAX = 100;

  /**
   * Busca profesionales con paginación por cursor.
   * Endpoint: GET /api/servicios/profesionales/buscar-paginado?dni=X&nombre=Y&size=10&cursor=&direction=NEXT
   */
  async buscarPaginado(
    dni: string | undefined,
    nombre: string | undefined,
    cursor: string | null,
    size = 10,
    origen: string | undefined,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST' = 'NEXT'
  ): Promise<ProfesionalPageResponse> {
    this.validarPaginacion(size);

    let params = new HttpParams().set('size', size.toString()).set('direction', direction);

    if (dni?.trim()) params = params.set('dni', dni.trim());
    if (nombre?.trim()) params = params.set('nombre', nombre.trim());
    if (origen?.trim()) params = params.set('origen', origen.trim());
    if (cursor) params = params.set('cursor', cursor);

    return firstValueFrom(
      this.http.get<ProfesionalPageResponse>(`${this.baseUrl}/buscar-paginado`, { params })
    );
  }

  private validarPaginacion(size: number): void {
    if (!Number.isInteger(size) || size < HttpProfesionalRepository.PAGE_SIZE_MIN || size > HttpProfesionalRepository.PAGE_SIZE_MAX) {
      throw new Error(
        `size debe estar entre ${HttpProfesionalRepository.PAGE_SIZE_MIN} y ${HttpProfesionalRepository.PAGE_SIZE_MAX}.`
      );
    }
  }
}
